import { TaskType } from '@weave/schema-gen-ts/dist/schemas/task-center/shared/v1/enums.pb';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import { CallIntelTypes } from '@frontend/api-call-intel';
import { AnalyticsHooks, AnalyticsUtils } from '@frontend/analytics-shared';
import { callIntelUtils } from '@frontend/call-intel-core';
import { CallIntelPrivateCommsBannerSource } from '@frontend/high-value-adoption';
import { validateUUIDV4 } from '@frontend/string';
import type { CallIntelSubView } from '../components/call-intelligence/hooks/use-call-intel-store';

dayjs.extend(quarterOfYear);
dayjs.extend(isBetween);

const { timePeriodLabels } = AnalyticsHooks;

export const callIntelligenceUtils = {
  /**
   * To get the drill down filter keys for overview page
   */
  getDrillDownFilterKeys: ({ id, type }: CallIntelSubView): CallIntelTypes.DrillDownOptions | undefined => {
    if (!id || !type) return undefined;

    switch (type) {
      case 'appointment-type':
        return {
          index: CallIntelTypes.FilterTypeEnum.FILTER_BY_APPOINTMENT_TYPE,
          key: 'appointment_types',
          value: [id],
        };

      case 'category':
        return {
          index: CallIntelTypes.FilterTypeEnum.FILTER_BY_CATEGORY,
          key: 'categories',
          value: [id],
        };

      case 'sentiment':
        return {
          index: CallIntelTypes.FilterTypeEnum.FILTER_BY_SENTIMENT,
          key: 'sentiments',
          value: [id],
        };

      case 'service-quality':
        return {
          index: CallIntelTypes.FilterTypeEnum.FILTER_BY_SERVICE_QUALITY,
          key: 'service_quality_flags',
          value: [id],
        };

      default:
        return undefined;
    }
  },
  /**
   * @param drillDownFilters - The drill-down filter options
   * @returns The appropriate banner source or 'all-call-recordings' as fallback
   */
  getPrivateCommsBannerSource: (
    drillDownFilters?: CallIntelTypes.DrillDownOptions
  ): CallIntelPrivateCommsBannerSource => {
    if (!drillDownFilters?.key) {
      return 'all-call-recordings';
    }

    switch (drillDownFilters.key) {
      case 'appointment_types':
        return 'appointment-type-drilldown';

      case 'categories':
        return 'category-drilldown';

      case 'sentiments':
        return 'sentiment-drilldown';

      case 'service_quality_flags':
        return 'service-issue-drilldown';

      default:
        return 'all-call-recordings';
    }
  },
};

export const findCitationTime = (transcript: string, citationText: string): string => {
  if (!citationText) {
    return '';
  }

  const parsedTranscript = parseTranscript(transcript);

  for (const entry of parsedTranscript) {
    if (entry.text.includes(citationText)) {
      return entry.time.start;
    }
  }

  return '';
};

export const parseTranscript = (transcript: string, tokens?: Record<string, string | undefined>) => {
  const regex = /\[(.*?)\] <(.*?) --> (.*?)> (.+?)\n/g;
  const result = [];
  let match;

  while ((match = regex.exec(transcript)) !== null) {
    const time = {
      start: AnalyticsUtils.toHHmmss(parseFloat(match[2]), true),
      end: AnalyticsUtils.toHHmmss(parseFloat(match[3]), true),
    };
    const speaker = match[1];
    const text = match[4];

    result.push({ time, speaker, text: !tokens ? text : parseTranscriptWithTokens(text, tokens) });
  }

  return result;
};

export const parseTranscriptWithTokens = (transcript: string, tokens: Record<string, string | undefined>): string => {
  if (!transcript) {
    return '';
  }
  return transcript.replace(/\[([^\]]+)\]/g, (match, p1) => tokens[p1] || match);
};

export const getFilteredEnumValues = <T extends Record<string, string>>(enumObj: T, excludeValue: T[keyof T]) =>
  Object.values(enumObj).filter((value) => value !== excludeValue);

export const fillTaskTypes = (
  partialTaskTypes: Partial<Record<TaskType, number>>,
  order: Exclude<TaskType, TaskType.TYPE_UNKNOWN>[]
): Record<Exclude<TaskType, TaskType.TYPE_UNKNOWN>, number> => {
  const filledTaskTypes = order.map((key) => [key, partialTaskTypes[key] ?? 0]);

  return Object.fromEntries(filledTaskTypes);
};

export const generateContactId = (call: CallIntelTypes.CallRecord) => {
  if (call.person?.id) {
    return call.person.id;
  } else if (call.phoneNumber?.nationalNumber) {
    return callIntelUtils.getPhoneNumber(call.phoneNumber);
  }
  return `unknown-contact-${call.id}`;
};

export const getChangeType = (rateChange: number): CallIntelTypes.ChangeType => {
  if (isNaN(rateChange)) return 'noData';
  if (rateChange > 0) return 'increased';
  if (rateChange < 0) return 'decreased';
  return 'unchanged';
};

export const getPreviousInterval = ({
  startDate,
  endDate,
  periodType,
}: CallIntelTypes.DateInterval & {
  periodType: string;
}): CallIntelTypes.DateInterval => {
  const start = dayjs(startDate);
  const end = dayjs(endDate);

  let previousStartDate;
  let previousEndDate;

  const matchingPeriodType = Object.keys(timePeriodLabels).find(
    (key) => timePeriodLabels[key] === periodType
  ) as keyof typeof AnalyticsUtils.defaultDateRangeMap;

  switch (matchingPeriodType) {
    case 'this-week': {
      previousStartDate = start.subtract(1, 'week').startOf('week');
      previousEndDate = start.subtract(1, 'week').endOf('week');
      break;
    }

    case 'this-month': {
      previousStartDate = start.subtract(1, 'month').startOf('month'); // Always 1st of previous month
      previousEndDate = start.subtract(1, 'month').endOf('month'); // Last day of previous month
      break;
    }

    case 'this-quarter': {
      previousStartDate = start.subtract(1, 'quarter').startOf('quarter');
      previousEndDate = start.subtract(1, 'quarter').endOf('quarter');
      break;
    }

    default: {
      const daysDiff = end.diff(start, 'day');
      previousStartDate = start.subtract(daysDiff + 1, 'day');
      previousEndDate = end.subtract(daysDiff + 1, 'day');
      break;
    }
  }

  return {
    startDate: previousStartDate.format('YYYY-MM-DDTHH:mm:ss'),
    endDate: previousEndDate.format('YYYY-MM-DDTHH:mm:ss'),
  };
};

export const isOngoingLabel = (label: string): boolean => {
  const today = dayjs();

  switch (label) {
    case timePeriodLabels.today:
    case timePeriodLabels['this-week']:
    case timePeriodLabels['this-month']:
    case timePeriodLabels['this-quarter']:
      return true;

    // If it's a custom range, check if it includes today.
    default:
      if (label.includes('-')) {
        const [startLabel, endLabel] = label.split(' - ').map((date) => dayjs(date.trim()));
        return today.isBetween(startLabel, endLabel, 'day', '[]');
      }

      // For all other cases (e.g., "Last Week", "Last Month"), return false
      return false;
  }
};

export const isContentSearchType = (searchType: CallIntelTypes.SearchTypeEnum) =>
  searchType === CallIntelTypes.SearchTypeEnum.SEARCH_BY_CONTENT;

export const cleanPhoneNumberSearchQuery = (query: string) => query.replace(/\D/g, '');

export const hasLetters = (value: string) => /[a-z]/i.test(value);

export const isNumberLikeSearchQuery = (value: string) => {
  const trimmedValue = value.trim();

  return (
    !!cleanPhoneNumberSearchQuery(trimmedValue) && !hasLetters(trimmedValue) && /^[\d\s().+-]+$/.test(trimmedValue)
  );
};

export const getDefaultSearchType = (value: string) => {
  return isNumberLikeSearchQuery(value)
    ? CallIntelTypes.SearchTypeEnum.SEARCH_BY_NUMBER
    : CallIntelTypes.SearchTypeEnum.SEARCH_BY_NAME;
};

export const getApiSearchQuery = (searchQuery: CallIntelTypes.SearchState): CallIntelTypes.SearchQuery => {
  const search = searchQuery.query.trim();

  if (!search) {
    return {
      search: '',
      searchType: searchQuery.searchType,
    };
  }

  const isUUIDSearch = validateUUIDV4(search);

  if (isUUIDSearch) {
    return {
      search,
      searchType: searchQuery.searchType,
    };
  }

  const isNumberLikeSearch = isNumberLikeSearchQuery(search);

  const shouldUseNumberSearch =
    searchQuery.searchType === CallIntelTypes.SearchTypeEnum.SEARCH_BY_NUMBER ||
    (searchQuery.searchType === CallIntelTypes.SearchTypeEnum.SEARCH_BY_NAME && isNumberLikeSearch);

  return {
    search: shouldUseNumberSearch && isNumberLikeSearch ? cleanPhoneNumberSearchQuery(search) : search,
    searchType: shouldUseNumberSearch ? CallIntelTypes.SearchTypeEnum.SEARCH_BY_NUMBER : searchQuery.searchType,
  };
};
